import { formatPersonName, formatPhoneNumber, type PersonNameParts } from '@/shared/lib';
import {
  EmployeeStatus,
  ImpersonationEndReason,
  OrganizationModule,
  type PlatformImpersonationSessionRowFieldsFragment,
  type PlatformUserRowFieldsFragment,
} from '@shared/api/graphql/generated';

export const platformModuleLabels: Record<OrganizationModule, string> = {
  [OrganizationModule.Booking]: 'Бронирование',
  [OrganizationModule.Orders]: 'Финансы',
  [OrganizationModule.Messenger]: 'Мессенджер',
  [OrganizationModule.Marketing]: 'Маркетинг',
  [OrganizationModule.Clients]: 'Клиенты',
  [OrganizationModule.Analytics]: 'Аналитика',
  [OrganizationModule.Radio]: 'Радио',
  [OrganizationModule.Sop]: 'Сценарии работы',
  [OrganizationModule.Loyalty]: 'Лояльность',
  [OrganizationModule.Shop]: 'Магазин',
  [OrganizationModule.TireService]: 'Шиномонтаж',
  [OrganizationModule.TireStorage]: 'Хранение шин',
  [OrganizationModule.Invoices]: 'Оплата по счёту',
};

/** Дополнения организация подключает сама: платформа их не включает и не показывает. */
const ADD_ON_MODULES: readonly OrganizationModule[] = [
  OrganizationModule.TireService,
  OrganizationModule.TireStorage,
  OrganizationModule.Radio,
  OrganizationModule.Invoices,
];

export const platformModules: OrganizationModule[] = Object.values(OrganizationModule).filter(
  (module) => !ADD_ON_MODULES.includes(module),
);

export const platformModuleOptions = platformModules.map((module) => ({
  value: module,
  label: platformModuleLabels[module],
}));

/**
 * ФИО пользователя платформы с деградацией до телефона и заглушки.
 * Единственный источник правды для всех платформенных экранов.
 */
export const getPlatformPersonName = (person: PersonNameParts & { phone?: string | null; id?: string }): string =>
  formatPersonName(person) || (person.phone ? formatPhoneNumber(person.phone) : (person.id ?? 'Без имени'));

export const getPlatformUserDisplayName = (row: PlatformUserRowFieldsFragment): string =>
  getPlatformPersonName(row.user);

export const getPlatformMembershipOrganizations = (row: PlatformUserRowFieldsFragment): string =>
  row.memberships.map(({ organization }) => organization.title).join(', ');

export const getPlatformMembershipForOrganization = (row: PlatformUserRowFieldsFragment, organizationId: string) =>
  row.memberships.find(({ organization }) => organization.id === organizationId);

export const getWorkingPlatformMemberships = (row: PlatformUserRowFieldsFragment) =>
  row.memberships.filter(({ employee }) => employee.status === EmployeeStatus.Working);

export const canImpersonatePlatformUser = (
  row: PlatformUserRowFieldsFragment,
  organizationId?: string | null,
): boolean => {
  const memberships = getWorkingPlatformMemberships(row);

  return organizationId
    ? memberships.some(({ organization }) => organization.id === organizationId)
    : memberships.length > 0;
};

export const getPlatformEmployeeStatusLabel = (status: EmployeeStatus): string =>
  status === EmployeeStatus.Working ? 'Работает' : 'Уволен';

export const platformSessionEndReasonLabels: Record<ImpersonationEndReason, string> = {
  [ImpersonationEndReason.Manual]: 'Вручную',
  [ImpersonationEndReason.Logout]: 'Через выход',
  [ImpersonationEndReason.SessionExpired]: 'Сессия истекла',
  [ImpersonationEndReason.Forced]: 'Принудительно',
  [ImpersonationEndReason.Superseded]: 'Вытеснена новым входом',
};

export const getPlatformSessionStatusLabel = (session: PlatformImpersonationSessionRowFieldsFragment): string => {
  if (!session.ended_at) {
    return 'Активна';
  }

  return session.end_reason ? platformSessionEndReasonLabels[session.end_reason] : 'Завершена';
};
